import { ref, computed, toValue, watch } from 'vue';
import { get } from 'lodash';
import { useQueryClient } from '@tanstack/vue-query';

import { AssetVisitEventType } from '@nova/core';
import { taskKeys } from '@/composables/queryKeys';
import {
  useAssetContainersByWarehouseId,
  useSpotsByWarehouseId,
  useSpotAreasByWarehouseId
} from '@/composables';
import yardTasksService from '@satellite/services/yard-tasks-service';

import type { MaybeRefOrGetter, Ref, ComputedRef } from 'vue';
import type { IAssetContainer, INovaEntity, ISpot } from '@nova/core';

interface ISpotArea extends INovaEntity {
  name: string;
  warehouseId: string;
  orgId: string;
}

interface IAssetContainerWithEvents extends IAssetContainer {
  assetContainerEvents?: Array<{ eventType: string }>;
}

interface ISpotAssignmentWithJoins {
  id: string;
  spotId: string;
  warehouseId: string;
  assetVisitId?: string | null;
  assetContainerId?: string | null;
  assetVisit?: {
    assetContainers?: IAssetContainerWithEvents[];
  };
  assetContainer?: IAssetContainerWithEvents;
}

interface ISpotWithAssignments extends Omit<ISpot, 'spotAssignments'> {
  spotAreaId?: string;
  spotAreaName?: string;
  spotAssignments?: ISpotAssignmentWithJoins[];
}

interface IProcessedAsset {
  assetId: string;
  assetCode: string;
  icon: string;
  isAssigned: boolean;
  spotAreaName: string | null;
  spotId: string | null;
  spotCode: string | null;
  disabled: boolean;
}

interface ISpotAssignment {
  spotAreaName: string | null;
  spotCode: string | null;
  spotId: string | null;
}

interface IAreaData {
  area: ISpotArea;
  spots: ISpotWithAssignments[];
}

interface IMoveTaskChild {
  originSpot?: { id: string };
  destinationSpot?: { id: string };
}

interface ITask {
  child: IMoveTaskChild;
}

interface ITaskListResponse {
  items: ITask[];
}

type ActiveMoveTasksBySpotId = Record<string, ITask[]>;

interface IUseYardAssetSelectOptions {
  warehouseId?: MaybeRefOrGetter<string | null> | { value: string | null };
  params?: Record<string, unknown>;
  includeWaitingAssignmentAssets?: MaybeRefOrGetter<boolean | 'disabled'>;
  includeAssignedAssets?: MaybeRefOrGetter<boolean | 'disabled'>;
  includeAssociatedToActiveMoveTasks?: MaybeRefOrGetter<boolean | 'disabled'>;
  search?: MaybeRefOrGetter<string>;
  showDisabled?: MaybeRefOrGetter<boolean>;
}

interface IYardAssetSelect {
  assetContainersRef: Ref<IAssetContainerWithEvents[]>;
  assetsRef: ComputedRef<IProcessedAsset[]>;
  isLoadingRef: Ref<boolean>;
  fetchAssetContainers: () => Promise<IAssetContainerWithEvents[]>;
  fetchSpots: () => Promise<ISpotWithAssignments[]>;
  fetchSpotAreas: () => Promise<ISpotArea[]>;
  fetchActiveMoveTasks: (spotIds: string[]) => Promise<ActiveMoveTasksBySpotId>;
  fetchAll: () => Promise<void>;
  isAssetAssigned: (asset: IAssetContainerWithEvents) => boolean;
  isAssetInActiveMoveTask: (asset: IAssetContainerWithEvents) => boolean;
  isSpotInActiveMoveTask: (spotId: string) => boolean;
  getSpotAssignmentByAssetId: (assetContainerId: string) => ISpotAssignment;
}

/**
 * Exclude asset containers that have a Departed event.
 */
function excludeDepartedAssetContainers(
  assetContainers: IAssetContainerWithEvents[]
): IAssetContainerWithEvents[] {
  return assetContainers.filter(
    (ac: IAssetContainerWithEvents) =>
      !ac.assetContainerEvents?.some(e => e.eventType === AssetVisitEventType.Departed)
  );
}

/**
 * Composable for asset selection functionality.
 */
export function useYardAssetSelect(options: IUseYardAssetSelectOptions = {}): IYardAssetSelect {
  const {
    warehouseId,
    params = {},
    includeWaitingAssignmentAssets = false,
    includeAssignedAssets = false,
    includeAssociatedToActiveMoveTasks = false,
    search = '',
    showDisabled = true
  } = options;

  const queryClient = useQueryClient();

  const getWarehouseId = () => {
    const id = toValue(warehouseId as MaybeRefOrGetter<string | null>);
    return id || null;
  };

  const activeMoveTasksBySpotId = ref<ActiveMoveTasksBySpotId>({});

  const assetContainersQuery = useAssetContainersByWarehouseId<IAssetContainerWithEvents[]>(
    warehouseId as MaybeRefOrGetter<string | null | undefined>,
    () => params,
    {
      enabled: () => Boolean(getWarehouseId())
    }
  );

  const spotsQuery = useSpotsByWarehouseId<ISpotWithAssignments[]>(warehouseId, () => params, {
    enabled: () => Boolean(getWarehouseId()),
    joins: [
      'spotAssignments',
      'spotAssignments.assetVisit',
      'spotAssignments.assetVisit.assetContainers',
      'spotAssignments.assetContainer'
    ]
  });

  const spotAreasQuery = useSpotAreasByWarehouseId<ISpotArea[]>(
    warehouseId as MaybeRefOrGetter<string | null | undefined>,
    {},
    {
      enabled: computed(() => Boolean(getWarehouseId()))
    }
  );

  const assetContainers = computed(() => {
    const fetched =
      (assetContainersQuery.data.value as IAssetContainerWithEvents[] | undefined) || [];
    return excludeDepartedAssetContainers(fetched);
  });

  const spots = computed(() => (spotsQuery.data.value as ISpotWithAssignments[] | undefined) || []);
  const spotAreas = computed(() => (spotAreasQuery.data.value as ISpotArea[] | undefined) || []);
  const isLoading = computed(
    () =>
      assetContainersQuery.isFetching.value ||
      spotsQuery.isFetching.value ||
      spotAreasQuery.isFetching.value
  );

  const spotsByAreaMap = computed(() => {
    const spotsByArea = new Map<string, IAreaData>();

    spotAreas.value.forEach((area: ISpotArea) => {
      spotsByArea.set(area.id, {
        area,
        spots: []
      });
    });

    spots.value.forEach((spot: ISpotWithAssignments) => {
      if (!spot.spotAreaId) {
        return;
      }

      const areaData = spotsByArea.get(spot.spotAreaId);
      if (areaData) {
        areaData.spots.push(spot);
      }
    });

    return spotsByArea;
  });

  const getActiveSpotIds = (): string[] => {
    return Array.from(spotsByAreaMap.value.values()).flatMap((area: IAreaData) =>
      area.spots.map((spot: ISpotWithAssignments) => spot.id)
    );
  };

  /**
   * Get spot assignment information for an asset container by its ID.
   */
  const getSpotAssignmentByAssetId = (assetContainerId: string): ISpotAssignment => {
    const spot = spots.value.find((spot: ISpotWithAssignments) =>
      spot.spotAssignments?.find(
        (sa: ISpotAssignmentWithJoins) =>
          sa.assetContainerId === assetContainerId ||
          sa.assetVisit?.assetContainers?.find(
            (ac: IAssetContainerWithEvents) => ac.id === assetContainerId
          )
      )
    );

    if (!spot) {
      return {
        spotAreaName: null,
        spotCode: null,
        spotId: null
      };
    }

    const spotArea = spotsByAreaMap.value.get(spot.spotAreaId || '')?.area ?? null;

    return {
      spotAreaName: spotArea?.name ?? spot.spotAreaName ?? null,
      spotCode: spot.code ?? null,
      spotId: spot.id ?? null
    };
  };

  /**
   * Check if an asset container object is assigned to a spot.
   */
  const isAssetAssigned = (asset: IAssetContainerWithEvents): boolean => {
    const spotAssignment = getSpotAssignmentByAssetId(asset.id);
    return spotAssignment.spotId !== null;
  };

  /**
   * Check if a spot is part of an active move task.
   */
  const isSpotInActiveMoveTask = (spotId: string): boolean => {
    if (!spotId) {
      return false;
    }
    return Boolean(activeMoveTasksBySpotId.value[spotId]?.length);
  };

  /**
   * Check if an asset container is assigned to a spot that is part of an active move task.
   */
  const isAssetInActiveMoveTask = (asset: IAssetContainerWithEvents): boolean => {
    if (!isAssetAssigned(asset)) {
      return false;
    }

    const spotAssignment = getSpotAssignmentByAssetId(asset.id);
    return isSpotInActiveMoveTask(spotAssignment.spotId || '');
  };

  /**
   * Fetch spot areas for the warehouse.
   */
  const fetchSpotAreas = async (): Promise<ISpotArea[]> => {
    if (!getWarehouseId()) {
      console.warn('[useYardAssetSelect] No warehouseId provided to fetchSpotAreas');
      return [];
    }

    try {
      await spotAreasQuery.refetch();
      return spotAreas.value;
    } catch (error) {
      console.error('[useYardAssetSelect] Failed to fetch spot areas:', error);
      return [];
    }
  };

  /**
   * Fetch spots for the warehouse.
   */
  const fetchSpots = async (): Promise<ISpotWithAssignments[]> => {
    if (!getWarehouseId()) {
      console.warn('[useYardAssetSelect] No warehouseId provided to fetchSpots');
      return [];
    }

    try {
      await spotsQuery.refetch();
      return spots.value;
    } catch (error) {
      console.error('[useYardAssetSelect] Failed to fetch spots:', error);
      return [];
    }
  };

  /**
   * Fetch active move tasks for the given spot IDs.
   */
  const fetchActiveMoveTasks = async (spotIds: string[]): Promise<ActiveMoveTasksBySpotId> => {
    if (!Array.isArray(spotIds) || spotIds.length === 0) {
      activeMoveTasksBySpotId.value = {};
      return {};
    }

    try {
      const params = {
        statuses: 'created,in-progress',
        type: 'move'
      };

      const response = await queryClient.fetchQuery<ITaskListResponse>({
        queryKey: taskKeys.activeMoveTasks(params),
        queryFn: ({ signal }) =>
          yardTasksService.getYardTaskList({
            requestConfig: {
              params,
              signal
            }
          }) as Promise<ITaskListResponse>
      });

      const tasks = get(response, 'items') || ([] as ITask[]);

      const tasksBySpotId: ActiveMoveTasksBySpotId = {};
      spotIds.forEach(spotId => {
        tasksBySpotId[spotId] = tasks.filter((task: ITask) => {
          const originSpotId = get(task, 'child.originSpot.id') as string | undefined;
          const destinationSpotId = get(task, 'child.destinationSpot.id') as string | undefined;
          return originSpotId === spotId || destinationSpotId === spotId;
        });
      });

      activeMoveTasksBySpotId.value = tasksBySpotId;
      return tasksBySpotId;
    } catch (error) {
      console.warn('[useYardAssetSelect] Failed to fetch active move tasks:', error);
      activeMoveTasksBySpotId.value = {};
      return {};
    }
  };

  /**
   * Fetch asset containers for the warehouse.
   */
  const fetchAssetContainers = async (): Promise<IAssetContainerWithEvents[]> => {
    if (!getWarehouseId()) {
      console.warn('[useYardAssetSelect] No warehouseId provided to fetchAssetContainers');
      return [];
    }

    try {
      await assetContainersQuery.refetch();
      return assetContainers.value;
    } catch (error) {
      console.error('[useYardAssetSelect] Failed to fetch asset containers:', error);
      return [];
    }
  };

  /**
   * Fetch all required data (asset containers, spots, spot areas, active move tasks).
   * This is a convenience method to fetch everything at once.
   */
  const fetchAll = async (): Promise<void> => {
    if (!getWarehouseId()) {
      console.warn('[useYardAssetSelect] No warehouseId provided to fetchAll');
      return Promise.resolve();
    }

    await Promise.all([
      assetContainersQuery.refetch(),
      spotAreasQuery.refetch(),
      spotsQuery.refetch()
    ]);
  };

  watch(
    () => spots.value,
    () => {
      const spotIds = getActiveSpotIds();
      void fetchActiveMoveTasks(spotIds);
    },
    { immediate: true }
  );

  /**
   * Check if asset should be excluded based on filters
   */
  const shouldExcludeWaitingAssignmentAsset = (asset: IAssetContainerWithEvents): boolean => {
    return !isAssetAssigned(asset);
  };

  const shouldExcludeAssignedAsset = (asset: IAssetContainerWithEvents): boolean => {
    return isAssetAssigned(asset);
  };

  const shouldExcludeAssetAssociatedToActiveMoveTask = (
    asset: IAssetContainerWithEvents
  ): boolean => {
    return isAssetInActiveMoveTask(asset);
  };

  /**
   * Check if asset should be disabled (included but not selectable)
   */
  const shouldDisableAsset = (asset: IAssetContainerWithEvents): boolean => {
    const includeWaitingValue = toValue(includeWaitingAssignmentAssets);
    const includeAssignedValue = toValue(includeAssignedAssets);
    const includeMoveTaskValue = toValue(includeAssociatedToActiveMoveTasks);

    const includeWaitingDisabled = includeWaitingValue === 'disabled' && !isAssetAssigned(asset);
    const includeAssignedDisabled = includeAssignedValue === 'disabled' && isAssetAssigned(asset);
    const includeMoveTaskDisabled =
      includeMoveTaskValue === 'disabled' && isAssetInActiveMoveTask(asset);

    return includeWaitingDisabled || includeAssignedDisabled || includeMoveTaskDisabled;
  };

  /**
   * Process and format assets for display in select component
   */
  const processedAssets = computed(() => {
    const rawAssets = assetContainers.value;
    const searchQuery = toValue(search)?.trim().toLowerCase() || '';
    const showDisabledValue = toValue(showDisabled);

    const includeWaitingValue = toValue(includeWaitingAssignmentAssets);
    const includeAssignedValue = toValue(includeAssignedAssets);
    const includeMoveTaskValue = toValue(includeAssociatedToActiveMoveTasks);

    const exclusions = [
      ...(includeWaitingValue ? [] : [shouldExcludeWaitingAssignmentAsset]),
      ...(includeAssignedValue ? [] : [shouldExcludeAssignedAsset]),
      ...(includeMoveTaskValue ? [] : [shouldExcludeAssetAssociatedToActiveMoveTask])
    ];

    const isExcludedBySomeFilter = (asset: IAssetContainerWithEvents): boolean =>
      exclusions.some(exclude => exclude(asset));

    /**
     * Helper function to check if a string value matches the search query.
     * Handles null/undefined values and performs case-insensitive matching.
     */
    const matchesSearchString = (value: string | null | undefined): boolean => {
      if (!value || !searchQuery) {
        return false;
      }
      return value.toLowerCase().includes(searchQuery);
    };

    const matchesSearch = (asset: IAssetContainerWithEvents): boolean => {
      if (!searchQuery) {
        return true;
      }
      const assetCodeMatch = matchesSearchString(asset.code);
      const spotAssignment = getSpotAssignmentByAssetId(asset.id);
      const spotAreaNameMatch = matchesSearchString(spotAssignment.spotAreaName);
      const spotCodeMatch = matchesSearchString(spotAssignment.spotCode);

      return assetCodeMatch || spotAreaNameMatch || spotCodeMatch;
    };

    const assets = rawAssets
      .filter(asset => {
        if (isExcludedBySomeFilter(asset) || !matchesSearch(asset)) {
          return false;
        }

        if (!showDisabledValue && shouldDisableAsset(asset)) {
          return false;
        }

        return true;
      })
      .map(asset => {
        const spotAssignment = getSpotAssignmentByAssetId(asset.id);

        return {
          assetId: asset.id,
          assetCode: asset.code,
          icon: isAssetAssigned(asset) ? 'mdi-truck-trailer' : 'mdi-clock-outline',
          isAssigned: spotAssignment.spotId !== null,
          spotAreaName: spotAssignment.spotAreaName,
          spotCode: spotAssignment.spotCode,
          spotId: spotAssignment.spotId,
          disabled: shouldDisableAsset(asset)
        };
      })
      .sort((a, b) => {
        return a.assetCode.localeCompare(b.assetCode);
      });

    return assets;
  });

  return {
    assetContainersRef: computed(() => assetContainers.value) as Ref<IAssetContainerWithEvents[]>,
    assetsRef: processedAssets,
    isLoadingRef: computed(() => isLoading.value),

    fetchAssetContainers,
    fetchSpots,
    fetchSpotAreas,
    fetchActiveMoveTasks,
    fetchAll,

    isAssetAssigned,
    isAssetInActiveMoveTask,
    isSpotInActiveMoveTask,
    getSpotAssignmentByAssetId
  };
}
